import { SharingType } from 'constants/sharing';
import * as Types from './types';
import { Dispatch } from 'redux';
import {
  IAction,
  IActionMethods,
  KEEP_THE_SAME,
  StateStatus,
} from '../../../redux/utils/common';
import * as ProductService from 'services/api/product';
import * as CategoryService from 'services/api/category';
import * as PoitOfInterestService from 'services/api/pointofinterest';
import { IState } from './reducer';
import * as TaskService from 'services/api/task';
import * as FileService from 'services/api/file';
import EntityType from 'constants/entity';
import FileType from 'constants/fileType';
import FileContentType from 'constants/fileContentType';
import { IDocumentPayload } from 'services/api/pointofinterest';
import * as ParalistService from 'services/api/paralist';
import { ParalistGroupCodes } from 'constants/paralist';
import * as ClientServices from 'services/api/client';
import { getState } from 'redux/store';
import { ReducerKeys } from 'redux/config';
import * as SharingServices from 'services/api/sharing';
import * as GoogleServices from 'services/api/google';
import * as PointOfInterestCategoryServices from 'services/api/pointofinterestcategory';
import CommonActions from 'redux/reducers/gl_common_reducer/actions';
import { UserRoles } from 'constants/authRoles';
import { IFetchGoogleSearchPlaceInput } from 'services/api/google';
import * as PictureService from 'services/api/picture';

interface IFetchProductInput extends ProductService.IProductListInput {
  onFinish?: (filters: ProductService.FiltersOutput) => void;
}
class FetchProductList implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_PRODUCT_LIST,
      status: StateStatus.Pending,
      data: KEEP_THE_SAME,
    };
  }
  onSuccess(payload: {
    data: ProductService.IProductListOutput;
    page: number;
  }): IAction {
    return {
      type: Types.SET_PRODUCT_LIST,
      status: StateStatus.Success,
      data: payload,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_PRODUCT_LIST,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(payload: IFetchProductInput): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        const { onFinish, ...rest } = payload;
        dispatch(this.onPending());
        let response = await ProductService.fetchProductList(rest);
        onFinish && onFinish(response.data.filters);
        dispatch(
          this.onSuccess({
            data: response.data,
            page: response.data.paging.page,
          })
        );
      } catch (error) {
        console.log('FetchProductList Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

/* Fetch all categories */
class FetchCategory implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_CATEGORY,
      status: StateStatus.Pending,
      data: {},
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_CATEGORY,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_CATEGORY,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await CategoryService.fetchAllCategories();
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('FetchCategory Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

interface IAddProduct {
  data: ProductService.IAddProductInput;
  onFinish: (id: number) => void;
}
class AddNewProduct implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_ADD_PRODUCT,
      status: StateStatus.Pending,
      data: {},
    };
  }
  onSuccess(): IAction {
    return {
      type: Types.SET_ADD_PRODUCT,
      status: StateStatus.Success,
      data: {},
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_ADD_PRODUCT,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(payload: IAddProduct): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        const res = await ProductService.addProduct(payload.data);
        dispatch(this.onSuccess());
        payload.onFinish(res.data.id);
      } catch (error) {
        console.log('AddNewProduct Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class InsertProductFilter implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.INSERT_PRODUCT_FILTER,
      status: StateStatus.Pending,
      data: {},
    };
  }
  onSuccess(): IAction {
    return {
      type: Types.INSERT_PRODUCT_FILTER,
      status: StateStatus.Success,
      data: {},
    };
  }

  onFailed(): IAction {
    return {
      type: Types.INSERT_PRODUCT_FILTER,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(body: {
    payload: ProductService.IProductSharingPayload;
    onFilterEnd: (guid: string) => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        const sharingResult = await ProductService.insertProductSharing(
          body.payload
        );
        const guid = sharingResult.data.guid;
        body.onFilterEnd(guid);
        dispatch(this.onSuccess());
      } catch (error) {
        console.log('Insert filter sharing error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

/* Set filters selection */
class SetFiltersSelection implements IActionMethods {
  onFailed(result?: any): IAction {
    throw new Error('Method not implemented.');
  }
  onPending(result?: any): IAction {
    throw new Error('Method not implemented.');
  }
  onSuccess(data: IState['filtersSelection']): IAction {
    return {
      type: Types.SET_FILTERS_SELECTION,
      status: StateStatus.Success,
      data: data,
    };
  }

  action(data: IState['filtersSelection']): any {
    return async (dispatch: Dispatch<any>) => {
      dispatch(this.onSuccess(data));
    };
  }
}

class SetPointSelect implements IActionMethods {
  onFailed(result?: any): IAction {
    throw new Error('Method not implemented.');
  }
  onPending(result?: any): IAction {
    throw new Error('Method not implemented.');
  }
  onSuccess(data: IState['pointSelect']): IAction {
    return {
      type: Types.SET_POINT_SELECT,
      status: StateStatus.Success,
      data: data,
    };
  }

  action(data: IState['pointSelect']): any {
    return async (dispatch: Dispatch<any>) => {
      dispatch(this.onSuccess(data));
    };
  }
}

interface IFavouritePayload {
  id: number;
}
class AddToFavourite implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.ADD_TO_FAVOURITE,
      status: StateStatus.Pending,
      data: {},
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.ADD_TO_FAVOURITE,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.ADD_TO_FAVOURITE,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(payload: IFavouritePayload): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        await ProductService.addToFavourite({ id: payload.id });

        const products =
          (getState(ReducerKeys.PRODUCTS_REDUCER) as IState).products.data
            ?.items ?? [];

        const newProducts: Array<ProductService.IDataItems> = products.map(
          (value) =>
            value.id === payload.id ? { ...value, favourite: true } : value
        );

        dispatch(this.onSuccess({ products: newProducts }));
        dispatch(CommonActions.fetchInterestAction());
      } catch (error) {
        console.log('Add to favourite Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class RemoveFromFavourite implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.REMOVE_FROM_FAVOURITE,
      status: StateStatus.Pending,
      data: {},
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.REMOVE_FROM_FAVOURITE,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.REMOVE_FROM_FAVOURITE,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(payload: IFavouritePayload): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        await ProductService.removeFromFavourite({
          id: payload.id,
        });

        const products =
          (getState(ReducerKeys.PRODUCTS_REDUCER) as IState).products.data
            ?.items ?? [];

        const newProducts: Array<ProductService.IDataItems> = products.map(
          (value) =>
            value.id === payload.id ? { ...value, favourite: false } : value
        );

        dispatch(this.onSuccess({ products: newProducts }));
        dispatch(CommonActions.fetchInterestAction());
      } catch (error) {
        console.log('Remove favourite Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

// Get paralist
class FetchParalistPointOfInterestAction implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_PARALIST,
      status: StateStatus.Pending,
      data: KEEP_THE_SAME,
    };
  }
  onSuccess(payload: { data: ProductService.DataItemParalist[] }): IAction {
    return {
      type: Types.SET_PARALIST,
      status: StateStatus.Success,
      data: payload.data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_PARALIST,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ProductService.getParalistPointOfInterest();
        dispatch(
          this.onSuccess({
            data: response.data,
          })
        );
      } catch (error) {
        console.log('FetchParalistPointOfInterestAction Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class FetchPOIList implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_POI_LIST,
      status: StateStatus.Pending,
      data: KEEP_THE_SAME,
    };
  }
  onSuccess(payload: {
    data: ProductService.IProductListOutput;
    page: number;
  }): IAction {
    return {
      type: Types.SET_POI_LIST,
      status: StateStatus.Success,
      data: payload,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_POI_LIST,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(payload: PoitOfInterestService.IPointofInterestListInput): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await PoitOfInterestService.fetchPointofInterestList(
          payload
        );
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('Fetch POI Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

interface ISaveSearchByGuidPayload {
  guid: string;
  name: string;
  onFinish?: (success: boolean) => void;
}

class SaveSearchByGuid implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SAVE_SEARCH,
      status: StateStatus.Pending,
      data: {},
    };
  }

  onSuccess(): IAction {
    return {
      type: Types.SAVE_SEARCH,
      status: StateStatus.Success,
      data: {},
    };
  }

  onFailed(): IAction {
    return { type: Types.SAVE_SEARCH, status: StateStatus.Failed, data: {} };
  }

  action(payload: ISaveSearchByGuidPayload): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        await SharingServices.saveSearchByGuid(payload.guid, payload.name);
        dispatch(this.onSuccess());
        payload.onFinish && payload.onFinish(true);
      } catch (error) {
        console.log('Save Search By Guid Error:', error.message);
        dispatch(this.onFailed());
        payload.onFinish && payload.onFinish(false);
      }
    };
  }
}
/** Add POI  */
interface AddPOIInput {
  id?: number;
  guid?: string;
  name: string;
  category: number[];
  description: string;
  private: boolean;
  colour: string;
  files: any;
  documents: Array<IDocumentPayload> | null;
  location: {
    lat: number;
    lng: number;
  } | null;
  pictures?: IDocumentPayload[];
}
class AddPOI implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_ADD_POI,
      status: StateStatus.Pending,
      data: KEEP_THE_SAME,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_ADD_POI,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_ADD_POI,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action({
    body,
    onFinish,
  }: {
    body: AddPOIInput;
    onFinish: (success: boolean) => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        const { files, ...rest } = body;
        if (files) {
          const fileSizes = files?.map((file: any) => file?.src?.size);
          const dataPicture = files?.map((item: any) => {
            return item?.src;
          });
          const fuResponse: FileService.IUploadFileRes[] = await (
            await FileService.uploadMultipleFile(
              dataPicture,
              EntityType.Product
            )
          ).data;
          rest.documents = fuResponse.map((item, index) => ({
            mimetype: item.mimetype,
            virtualpath: item.path,
            originalfilename: item.originalname,
            filetype: FileType.Image,
            filecontenttype: FileContentType.Product,
            seofilename: '',
            altattribute: '',
            titleattribute: '',
            filesize: fileSizes[index],
            visible: true,
          }));
        }
        let response = await PoitOfInterestService.addPointofInterest(rest);
        dispatch(this.onSuccess(response.data));
        onFinish && onFinish(true);
      } catch (error) {
        console.log('Add POI Error:', error.message); // '<ClassName> Error: <error>'
        dispatch(this.onFailed());
      } finally {
        // add something
      }
    };
  }
}
class EditPOI implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_EDIT_POI,
      status: StateStatus.Pending,
      data: KEEP_THE_SAME,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_EDIT_POI,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_EDIT_POI,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action({
    body,
    onFinish,
  }: {
    body: AddPOIInput;
    onFinish: (success: boolean) => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        const { files, ...rest } = body;
        if (files) {
          const fileSizes = files?.map((file: any) => file?.src?.size);
          const dataPicture = files?.map((item: any) => {
            return item?.src;
          });
          const fuResponse: FileService.IUploadFileRes[] = await (
            await FileService.uploadMultipleFile(
              dataPicture,
              EntityType.Product
            )
          ).data;
          rest.documents = fuResponse.map((item, index) => ({
            mimetype: item.mimetype,
            virtualpath: item.path,
            originalfilename: item.originalname,
            filetype: FileType.Image,
            filecontenttype: FileContentType.Product,
            seofilename: '',
            altattribute: '',
            titleattribute: '',
            filesize: fileSizes[index],
            visible: true,
          }));
        }
        // if (rest.pictures) {
        //   const valueTemp = rest.documents ? rest.documents : [];
        //   rest.documents = valueTemp.concat(
        //     rest.pictures?.map((item) => ({
        //       mimetype: item.mimetype,
        //       virtualpath: item.virtualpath,
        //       originalfilename: item.originalfilename,
        //       filetype: FileType.Image,
        //       filecontenttype: FileContentType.Product,
        //       seofilename: '',
        //       altattribute: '',
        //       titleattribute: '',
        //       filesize: item.filesize,
        //       visible: item.visible,
        //     }))
        //   );
        // }
        const { pictures, ...dataSend } = rest;
        let response = await PoitOfInterestService.editPointofInterest(
          dataSend
        );
        dispatch(this.onSuccess(response.data));
        onFinish && onFinish(true);
      } catch (error) {
        console.log('Edit POI Error:', error.message); // '<ClassName> Error: <error>'
        dispatch(this.onFailed());
      } finally {
        // add something
      }
    };
  }
}
class DeletePOI implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_DELETE_POI,
      status: StateStatus.Pending,
      data: KEEP_THE_SAME,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_DELETE_POI,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_DELETE_POI,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action({
    id,
    onFinish,
  }: {
    id: number;
    onFinish: (success: boolean) => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await PoitOfInterestService.deletePointofInterest(id);
        dispatch(this.onSuccess(response.data));
        onFinish && onFinish(true);
      } catch (error) {
        console.log('Delete POI Error:', error.message); // '<ClassName> Error: <error>'
        dispatch(this.onFailed());
      } finally {
        // add something
      }
    };
  }
}
class SetSelectAddNew implements IActionMethods {
  onFailed(result?: any): IAction {
    throw new Error('Method not implemented.');
  }
  onPending(result?: any): IAction {
    throw new Error('Method not implemented.');
  }
  onSuccess(data: IState['selectAddNew']): IAction {
    return {
      type: Types.SET_SELECT_ADD_NEW,
      status: StateStatus.Success,
      data: data,
    };
  }

  action(data: IState['selectAddNew']): any {
    return async (dispatch: Dispatch<any>) => {
      dispatch(this.onSuccess(data));
    };
  }
}
// Task
class FetchCustomerName implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_CUSTOMER_NAME,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_CUSTOMER_NAME,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_CUSTOMER_NAME,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await TaskService.fetchCustomerNames();
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('Fetch customer names Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}
class FetchPropertyName implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_PROPERTY_NAME,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_PROPERTY_NAME,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_PROPERTY_NAME,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ProductService.fetchProductName();
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('Fetch property names Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class AddNewTask implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.ADD_NEW_TASK,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.ADD_NEW_TASK,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.ADD_NEW_TASK,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action({
    data,
    onFinish,
  }: {
    data: TaskService.ITask;
    onFinish: () => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        if (data?.pictures?.length > 0) {
          const dataPicture = data?.pictures?.map((item: any) => {
            return item?.src;
          });
          let response = await FileService.uploadMultipleFile(
            dataPicture,
            EntityType.Task
          );
          const documents = response?.data || [];
          const fullDocuments: any[] = documents.map(
            (document: {
              file: string;
              mimetype: string;
              originalname: string;
              path: string;
            }) => {
              let fileType = FileType.Image;
              if (document.mimetype === 'application/pdf') {
                fileType = FileType.Document;
              }
              return {
                file: document.file,
                mimetype: document.mimetype,
                seofilename: document.originalname,
                altattribute: document.originalname,
                titleattribute: document.originalname,
                virtualpath: document.path,
                originalfilename: document.originalname,
                filesize: 0,
                visible: true,
                filetype: fileType,
                filecontenttype: FileContentType.Task,
              };
            }
          );
          data.documents = fullDocuments;
          delete data.pictures;
        }
        const payloadSend = { ...data };
        delete payloadSend.onFinish;
        let response = await TaskService.addNewTask(payloadSend);
        dispatch(this.onSuccess(response.data));
        dispatch({
          type: Types.UPLOAD_FILE_TASK,
          data: null,
        });
        onFinish();
      } catch (error) {
        console.log('Add new task Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class UploadFileTask implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.UPLOAD_FILE_TASK,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.UPLOAD_FILE_TASK,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.UPLOAD_FILE_TASK,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(payload: { files: FileList }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await FileService.uploadMultipleFile(
          payload.files,
          EntityType.Task
        );
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('Upload file task Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class FetchStatusLane implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_STATUS_LANE,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_STATUS_LANE,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_STATUS_LANE,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ParalistService.getParalistByGroupcode(
          ParalistGroupCodes.TaskStatus // 'task.status'
        );
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('get paralist by group codeError:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

/** Fetch Client Favorites  */
interface IFetchClientFavoritesInput
  extends ClientServices.IFetchFavoritesInput {}

class FetchClientFavorites implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_FAVORITES,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: ClientServices.IFavoritesData): IAction {
    return {
      type: Types.SET_FAVORITES,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_FAVORITES,
      status: StateStatus.Failed,
      data: null,
    };
  }

  action(input: IFetchClientFavoritesInput): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ClientServices.fetchFavorites(input);
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('FetchClientFavorites Error:', error.message); // '<ClassName> Error: <error>'
        dispatch(this.onFailed());
      }
    };
  }
}

class FetchCustomers implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_CUSTOMERS,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_CUSTOMERS,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_CUSTOMERS,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ProductService.fetchCustomers();
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('Fetch Customers Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class FetchClient implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_CLIENTS_MINI_PROFILES,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_CLIENTS_MINI_PROFILES,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_CLIENTS_MINI_PROFILES,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ClientServices.fetchCustomers();
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('Fetch client Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}
class FetchCustomersByRole implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_CUSTOMERS_BY_ROLE,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_CUSTOMERS_BY_ROLE,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_CUSTOMERS_BY_ROLE,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action({ role }: { role: UserRoles }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ProductService.fetchCustomersByRole({ role });
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('fetchCustomersByRole Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class AddAssignToClients implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_ADD_ASSIGN_CLIENTS,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_ADD_ASSIGN_CLIENTS,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_ADD_ASSIGN_CLIENTS,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action({
    data,
    onFinish,
  }: {
    data: ProductService.IAssignToClientsInput;
    onFinish: () => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ProductService.addAssignToClients(data);
        dispatch(this.onSuccess(response.data));
        onFinish();
      } catch (error) {
        console.log('Add assign client Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class AddToCollections implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_ADD_COLLECTIONS,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_ADD_COLLECTIONS,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_ADD_COLLECTIONS,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action({
    data,
    onFinish,
  }: {
    data: ProductService.IAddToCollectionsInput;
    onFinish: () => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        if (data?.clientIds) {
          let response = await ProductService.addToCollections(data);
          dispatch(this.onSuccess(response.data));
          onFinish();
        } else {
          const { clientIds, ...rest } = data;
          let response = await ProductService.addToCollections(rest);
          dispatch(this.onSuccess(response.data));
          onFinish();
        }
      } catch (error) {
        console.log('Add collections Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

interface IAddSharingProductInput extends SharingServices.ISharingProductInput {
  productIds: number[];
  clientIds?: number[] | null;
}
class AddSharingProduct implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_ADD_SHARING_PRODUCT,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_ADD_SHARING_PRODUCT,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_ADD_SHARING_PRODUCT,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action({
    data,
    onFinish,
  }: {
    data: IAddSharingProductInput;
    onFinish: () => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        const { productIds, clientIds, ...rest } = data;
        dispatch(this.onPending());
        let response = await SharingServices.addSharingProduct(rest);
        if (clientIds) {
          await ProductService.addToCollections({
            productIds,
            collectionIds: [response.data.id],
            clientIds,
          });
        } else {
          await ProductService.addToCollections({
            productIds,
            collectionIds: [response.data.id],
          });
        }
        dispatch(this.onSuccess(response.data));
        onFinish();
      } catch (error) {
        console.log('Add sharing product Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class FetchClientCollectionPersonal implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_CLIENT_COLLECTION_PERSONAL,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: ClientServices.IClientCollectionsData): IAction {
    return {
      type: Types.SET_CLIENT_COLLECTION_PERSONAL,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_CLIENT_COLLECTION_PERSONAL,
      status: StateStatus.Failed,
      data: null,
    };
  }

  action(clientId: string | number): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ClientServices.fetchSingleClientCollections(
          clientId,
          {
            type: 'personal',
          }
        );
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('FetchClientPropertyCollections Error:', error.message); // '<ClassName> Error: <error>'
        dispatch(this.onFailed());
      }
    };
  }
}

class FetchSharingProduct implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_SHARING_PRODUCT,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_SHARING_PRODUCT,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_SHARING_PRODUCT,
      status: StateStatus.Failed,
      data: null,
    };
  }

  action(ids: number[]): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await SharingServices.sharingByListProduct({
          ids,
          sharingType: SharingType.Product,
        });
        if (response.status === 'SUCCESS' && !!!response.data) {
          let newResponse: any = await SharingServices.addSharingListProduct({
            ids,
            sharingType: SharingType.Product,
          });
          dispatch(this.onSuccess({ Guid: newResponse.data.guid }));
        } else {
          dispatch(this.onSuccess(response.data));
        }
      } catch (error) {
        console.log('FetchClientPropertyCollections Error:', error.message); // '<ClassName> Error: <error>'
        dispatch(this.onFailed());
      }
    };
  }
}
class SendEmailProduct implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SEND_EMAIL_PRODUCT,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SEND_EMAIL_PRODUCT,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SEND_EMAIL_PRODUCT,
      status: StateStatus.Failed,
      data: null,
    };
  }

  action(payload: {
    data: ProductService.ISendMailInput;
    onFinish: () => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ProductService.sendEmail(payload.data);
        dispatch(this.onSuccess(response.data));
        payload.onFinish();
      } catch (error) {
        console.log('Send Mail Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class SendEmailAdvanceProduct implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SEND_EMAIL_ADVANCE_PRODUCT,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SEND_EMAIL_ADVANCE_PRODUCT,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SEND_EMAIL_ADVANCE_PRODUCT,
      status: StateStatus.Failed,
      data: null,
    };
  }

  action(payload: {
    data: ProductService.ISendMailAdvanceInput;
    onFinish: () => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ProductService.sendEmailAdvance(payload.data);
        dispatch(this.onSuccess(response.data));
        payload.onFinish();
      } catch (error) {
        console.log('Send Mail Advance Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class SendCloneProduct implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SEND_CLONE_PRODUCT,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SEND_CLONE_PRODUCT,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SEND_CLONE_PRODUCT,
      status: StateStatus.Failed,
      data: null,
    };
  }

  action(payload: {
    data: ProductService.ISendCloneProductInput;
    onFinish: () => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ProductService.sendCloneProduct(payload.data);
        dispatch(this.onSuccess(response.data));
        payload.onFinish();
      } catch (error) {
        console.log('sendCloneProduct Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}
class FetchAssignees implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_ASSIGNESS,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_ASSIGNESS,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_ASSIGNESS,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await TaskService.fetchAssignees();
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('Fetch  Assigness Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class FetchListVieweds implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_LIST_VIEWED,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_LIST_VIEWED,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_LIST_VIEWED,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ProductService.getListViewed();
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('FetchListViewed Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

const SetPublished = (published: number) => {
  return {
    type: Types.SET_PUBLISHED,
    data: published,
  };
};

const SetCollapse = (isCollapse: boolean) => {
  return {
    type: Types.SET_COLLAPSE,
    data: isCollapse,
  };
};

const SetPolygonDraw = (list: any[]) => {
  return {
    type: Types.SET_POLYGON_DRAW,
    data: list,
  };
};
class DeleteClientCollection implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.DELETE_COLLECTION,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: ClientServices.IClientCollectionsData): IAction {
    return {
      type: Types.DELETE_COLLECTION,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.DELETE_COLLECTION,
      status: StateStatus.Failed,
      data: null,
    };
  }

  action(payload: {
    collectionId: string | number;
    payloadData: ClientServices.IDeleteClientCollectionsInput;
    onSuccessed: () => void | null;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ClientServices.deleteSingleClientCollections(
          payload.collectionId,
          payload.payloadData
        );
        payload.onSuccessed();
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('FetchClientPropertyCollections Error:', error.message); // '<ClassName> Error: <error>'
        dispatch(this.onFailed());
      }
    };
  }
}

class AddClientRequest implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_ADD_CLIENT_REQUEST,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_ADD_CLIENT_REQUEST,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_ADD_CLIENT_REQUEST,
      status: StateStatus.Failed,
      data: null,
    };
  }

  action(payload: {
    data: SharingServices.IAddClientRequestInput;
    onFinish: () => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await SharingServices.addClientRequest(payload.data);
        dispatch(this.onSuccess(response.data));
        payload.onFinish();
      } catch (error) {
        console.log('addClientRequest Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class FetchGoogleSearchPlace implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_GOOGLE_SEARCH_PLACE,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_GOOGLE_SEARCH_PLACE,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_GOOGLE_SEARCH_PLACE,
      status: StateStatus.Failed,
      data: null,
    };
  }

  action(payload: IFetchGoogleSearchPlaceInput): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await GoogleServices.fetchGoogleSearchPlace(payload);
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('fetchGoogleSearchPlace Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class GetCollectionByGuid implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.GET_COLLECTION_BY_GUID,
      status: StateStatus.Pending,
      data: {},
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.GET_COLLECTION_BY_GUID,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.GET_COLLECTION_BY_GUID,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(guid: string): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        const response = await SharingServices.getCollectionByGuid(guid);
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('get collection by guid error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

const resetDataCollection = () => {
  return {
    type: Types.RESET_DATA_COLLECTION,
  };
};

class FetchPOICategoryListAction implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.GET_LIST_POI_CATEGORY,
      status: StateStatus.Pending,
      data: {},
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.GET_LIST_POI_CATEGORY,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.GET_LIST_POI_CATEGORY,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        const response =
          await PointOfInterestCategoryServices.fetchPointOfInterestCategoryList(
            {
              sortField: 'datecreated',
              page: 0,
              pageSize: 99999,
              order: 'desc',
            }
          );
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('get collection by guid error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}
class AddPOICategory implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_ADD_POI_CATEGORY,
      status: StateStatus.Pending,
      data: {},
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_ADD_POI_CATEGORY,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_ADD_POI_CATEGORY,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action({
    data,
    onFinish,
  }: {
    data: PointOfInterestCategoryServices.IPointOfInterestCategoryAddInput;
    onFinish: () => void;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        const { picture, ...body } = data;
        if (picture && typeof picture !== 'number') {
          const fuResponse: FileService.IUploadFileRes = (
            await FileService.uploadFile(picture, EntityType.Product)
          ).data;
          const insertPictureRespone = await PictureService.insertPicture({
            documents: [
              {
                mimetype: fuResponse.mimetype,
                virtualpath: fuResponse.path,
                seofilename: '',
                altattribute: '',
                titleattribute: '',
                originalfilename: fuResponse.originalname,
                filesize: picture.size,
                visible: true,
                filetype: FileType.Image,
                filecontenttype: FileContentType.Product,
              },
            ],
          });
          body['pictureid'] = insertPictureRespone?.data?.[0];
        }
        const response =
          await PointOfInterestCategoryServices.addPointOfInterestCategory(
            body
          );
        onFinish();
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('addPointOfInterestCategory error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class FetchProductDocument implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_PRODUCT_DOCUMENT,
      status: StateStatus.Pending,
      data: KEEP_THE_SAME,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_PRODUCT_DOCUMENT,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_PRODUCT_DOCUMENT,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(id: string | number): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response: any = await ProductService.fetchProductDocument(id);
        const convertData = response.data?.map((item: any) => {
          return {
            ...item,
            productId: id,
          };
        });
        console.log('convertData', convertData);
        dispatch(this.onSuccess(convertData));
      } catch (error) {
        console.log('FetchProductDocument Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

const setMapZoomLevel = (data: number) => {
  return {
    type: Types.SET_MAP_ZOOM_LEVEL,
    data: data,
  };
};

class ResetDocument implements IActionMethods {
  onFailed(): IAction {
    throw new Error('Method not implemented.');
  }
  onPending(): IAction {
    throw new Error('Method not implemented.');
  }
  onSuccess(): IAction {
    return {
      type: Types.RESET_DOCUMENT,
      status: StateStatus.Success,
      data: {},
    };
  }

  action(): any {
    return async (dispatch: Dispatch<any>) => {
      dispatch(this.onSuccess());
    };
  }
}

class DeletePropertyFromCollection implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.DELETE_PROPERTY_FROM_COLLECTION,
      status: StateStatus.Pending,
      data: {},
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.DELETE_PROPERTY_FROM_COLLECTION,
      status: StateStatus.Success,
      data: data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.DELETE_PROPERTY_FROM_COLLECTION,
      status: StateStatus.Failed,
      data: {},
    };
  }

  action(payload: {
    data: {
      propertyIds: number[];
      guid: string;
    };
    onFinish: (params: boolean) => void | null;
  }): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ProductService.deletePropertyFromCollection(
          payload.data
        );
        dispatch(this.onSuccess(response.data));
        payload.onFinish(true);
      } catch (error) {
        console.log('Deleta property from collection Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

class FetchAssignClientsAction implements IActionMethods {
  onPending(): IAction {
    return {
      type: Types.SET_ASSIGN_CLIENTS_PRODUCTS,
      status: StateStatus.Pending,
      data: null,
    };
  }
  onSuccess(data: any): IAction {
    return {
      type: Types.SET_ASSIGN_CLIENTS_PRODUCTS,
      status: StateStatus.Success,
      data,
    };
  }

  onFailed(): IAction {
    return {
      type: Types.SET_ASSIGN_CLIENTS_PRODUCTS,
      status: StateStatus.Failed,
      data: null,
    };
  }

  action(clientId: string | number): any {
    return async (dispatch: Dispatch<any>) => {
      try {
        dispatch(this.onPending());
        let response = await ProductService.getAssignToClients({
          clientid: clientId as number,
        });
        dispatch(this.onSuccess(response.data));
      } catch (error) {
        console.log('FetchAssignClientsAction Error:', error.message);
        dispatch(this.onFailed());
      }
    };
  }
}

export default {
  fetchProductListAction: (payload: IFetchProductInput) =>
    new FetchProductList().action(payload),
  fetchCategoryAction: () => new FetchCategory().action(),
  setFiltersSelectionAction: (data: IState['filtersSelection']) =>
    new SetFiltersSelection().action(data),
  addNewProductAction: (payload: IAddProduct) =>
    new AddNewProduct().action(payload),
  insertProductFilterAction: (
    payload: ProductService.IProductSharingPayload,
    onFilterEnd: (guid: string) => void
  ) => new InsertProductFilter().action({ payload, onFilterEnd }),
  addToFavourite: (payload: IFavouritePayload) =>
    new AddToFavourite().action(payload),
  removeFromFavourite: (payload: IFavouritePayload) =>
    new RemoveFromFavourite().action(payload),
  setPointSelect: (data: IState['pointSelect']) =>
    new SetPointSelect().action(data),
  fetchParalistPointOfInterestAction: () =>
    new FetchParalistPointOfInterestAction().action(),
  fetchPOIListAction: (
    payload: PoitOfInterestService.IPointofInterestListInput
  ) => new FetchPOIList().action(payload),
  saveSearchByGuidAction: (payload: ISaveSearchByGuidPayload) =>
    new SaveSearchByGuid().action(payload),
  addPOIAction: (payload: {
    body: AddPOIInput;
    onFinish: (success: boolean) => void;
  }) => new AddPOI().action(payload),
  editPOIAction: (payload: {
    body: AddPOIInput;
    onFinish: (success: boolean) => void;
  }) => new EditPOI().action(payload),
  deletePOIAction: (payload: {
    id: number;
    onFinish: (success: boolean) => void;
  }) => new DeletePOI().action(payload),
  setSelectAddNew: (data: IState['selectAddNew']) =>
    new SetSelectAddNew().action(data),
  fetchCustomerName: () => new FetchCustomerName().action(),
  fetchPropertyName: () => new FetchPropertyName().action(),
  addNewTask: ({
    data,
    onFinish,
  }: {
    data: TaskService.ITask;
    onFinish: () => void;
  }) => new AddNewTask().action({ data, onFinish }),
  uploadFileTask: (payload: { files: FileList }) =>
    new UploadFileTask().action(payload),
  fetchStatusLane: () => new FetchStatusLane().action(),
  fetchClientFavoritesAction: (payload: IFetchClientFavoritesInput) =>
    new FetchClientFavorites().action(payload),
  fetchCustomersAction: () => new FetchCustomers().action(),
  fetchClientAction: () => new FetchClient().action(),
  fetchCustomersByRoleAction: ({ role }: { role: UserRoles }) =>
    new FetchCustomersByRole().action({ role }),
  addAssignToClientsAction: ({
    data,
    onFinish,
  }: {
    data: ProductService.IAssignToClientsInput;
    onFinish: () => void;
  }) => new AddAssignToClients().action({ data, onFinish }),
  addAddToCollectionsAction: ({
    data,
    onFinish,
  }: {
    data: ProductService.IAddToCollectionsInput;
    onFinish: () => void;
  }) => new AddToCollections().action({ data, onFinish }),
  addSharingProduct: ({
    data,
    onFinish,
  }: {
    data: IAddSharingProductInput;
    onFinish: () => void;
  }) => new AddSharingProduct().action({ data, onFinish }),
  fetchClientCollectionsPersonalAction: (clientId: string | number) =>
    new FetchClientCollectionPersonal().action(clientId),
  fetchSharingProductAction: (ids: number[]) =>
    new FetchSharingProduct().action(ids),
  fetchAssignees: () => new FetchAssignees().action(),
  fetchListVieweds: () => new FetchListVieweds().action(),
  sendEmailProductAction: (payload: {
    data: ProductService.ISendMailInput;
    onFinish: () => void;
  }) => new SendEmailProduct().action(payload),
  sendEmailAdvanceProductAction: (payload: {
    data: ProductService.ISendMailAdvanceInput;
    onFinish: () => void;
  }) => new SendEmailAdvanceProduct().action(payload),
  sendCloneProductAction: (payload: {
    data: ProductService.ISendCloneProductInput;
    onFinish: () => void;
  }) => new SendCloneProduct().action(payload),
  setPublishedAction: (published: number) => SetPublished(published),
  setCollapseAction: (isCollapse: boolean) => SetCollapse(isCollapse),
  deleteClientCollectionsAction: (payload: {
    collectionId: string | number;
    payloadData: ClientServices.IDeleteClientCollectionsInput;
    onSuccessed: () => void | null;
  }) => new DeleteClientCollection().action(payload),
  addClientRequestAction: (payload: {
    data: SharingServices.IAddClientRequestInput;
    onFinish: () => void;
  }) => new AddClientRequest().action(payload),
  SetPolygonDraw: SetPolygonDraw,
  fetchGoogleSearchPlace: (payload: IFetchGoogleSearchPlaceInput) =>
    new FetchGoogleSearchPlace().action(payload),
  fetchCollectionByGuid: (guid: string) =>
    new GetCollectionByGuid().action(guid),
  resetDataCollection: () => resetDataCollection(),
  fetchPOICategoryListAction: () => new FetchPOICategoryListAction().action(),
  addPOICategoryAction: (payload: {
    data: PointOfInterestCategoryServices.IPointOfInterestCategoryAddInput;
    onFinish: () => void;
  }) => new AddPOICategory().action(payload),
  fetchProductDocumentAction: (id: string | number) =>
    new FetchProductDocument().action(id),
  resetDocumentAction: () => new ResetDocument().action(),
  setMapZoomLevel: (data: number) => setMapZoomLevel(data),
  deletePropertyFromCollectionAction: (payload: {
    data: {
      propertyIds: number[];
      guid: string;
    };
    onFinish: (params: boolean) => void | null;
  }) => new DeletePropertyFromCollection().action(payload),
  fetchAssignClientsAction: ({ clientId }: { clientId: number }) =>
    new FetchAssignClientsAction().action(clientId),
};
